import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import { signagePlaybackLabel } from "../utils/devicePresentation";
import type { Translate } from "../i18n/translate";
import { DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS } from "@shared/types/foyer";
import type { SignagePlaybackReport } from "@shared/types/foyer";

/**
 * Is this screen actually playing, right now?
 *
 * ## Why the schedule cannot answer this
 *
 * Everything else on a screen card is resolved from the TIMELINE - what the
 * screen is *meant* to play. That is a statement about configuration and it is
 * true whether the panel is on, off, unplugged or showing a firmware dialog.
 * The strip beside this badge would look identical for a wall that has been
 * dark since Tuesday.
 *
 * So this reads the panel's own heartbeat and nothing else, and it is the only
 * thing on the card that does.
 *
 * ## Offline beats everything the panel last said
 *
 * A report is a claim made at a moment. A screen that reported `playing` and
 * then stopped reporting is not playing - it is gone, and its last words are
 * the most misleading thing we could show. Liveness is therefore checked
 * FIRST, against the same window the fleet uses.
 *
 * ## Playing is not the same as healthy
 *
 * A loop that has quietly lost three files to 404s still reports `playing`.
 * `signagePlaybackLabel` already encodes that distinction for the fleet list,
 * so it is reused rather than re-derived here - two implementations of "is
 * this screen alright" disagreeing is exactly the confusion this badge exists
 * to remove.
 */

export interface SignageLiveBadgeProps {
  /** ISO timestamp of the panel's last heartbeat, or null if never seen. */
  lastSeenAt: string | null | undefined;
  /** The panel's own account of what it is playing. */
  report: SignagePlaybackReport | undefined;
  /** Ticks with the page clock so "online" expires without a refresh. */
  now: Date;
}

type Tone = "live" | "warn" | "quiet" | "dead";

const TONES: Record<Tone, { fg: string; bg: string }> = {
  live: { fg: tokens.brand, bg: tokens.brandTintSoft },
  warn: { fg: "#B4651A", bg: "rgba(180,101,26,0.12)" },
  quiet: { fg: tokens.inkMuted, bg: "rgba(128,128,128,0.12)" },
  dead: { fg: "#9E2A24", bg: "rgba(158,42,36,0.12)" },
};

export function liveState(
  t: Translate,
  props: SignageLiveBadgeProps
): {
  text: string;
  tone: Tone;
  detail: string;
} {
  const { lastSeenAt, report, now } = props;
  const seen = Date.parse(lastSeenAt ?? "");
  const online =
    Number.isFinite(seen) &&
    now.getTime() - seen <= DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS * 1000;

  if (!Number.isFinite(seen)) {
    return {
      text: t("signage:signageLiveBadge.neverSeen"),
      tone: "quiet",
      detail: t("signage:signageLiveBadge.neverSeenDetail"),
    };
  }
  if (!online) {
    // Deliberately NOT "was playing". The last thing a panel said before it
    // went quiet is the most misleading thing to put on screen.
    return {
      text: t("signage:signageLiveBadge.offline"),
      tone: "dead",
      detail: t("signage:signageLiveBadge.offlineDetail"),
    };
  }

  const label = signagePlaybackLabel(t, report);
  if (!label) {
    return {
      text: t("signage:signageLiveBadge.online"),
      tone: "quiet",
      detail: t("signage:signageLiveBadge.onlineDetail"),
    };
  }
  if (label.severity === "error") {
    return {
      text: t("signage:signageLiveBadge.notPlaying"),
      tone: "dead",
      detail: label.detail,
    };
  }
  if (report?.state === "idle") {
    // Correct and common - a screen outside its hours. Not a fault, and
    // colouring it like one trains an operator to ignore the badge.
    return {
      text: t("signage:signageLiveBadge.idle"),
      tone: "quiet",
      detail: label.detail,
    };
  }
  // Playing, but the loop is quietly shorter than the operator built it.
  if (label.severity === "warn") {
    return {
      text: t("signage:signageLiveBadge.live"),
      tone: "warn",
      detail: label.detail,
    };
  }
  return {
    text: t("signage:signageLiveBadge.live"),
    tone: "live",
    detail: label.detail,
  };
}

export default function SignageLiveBadge(props: SignageLiveBadgeProps) {
  const { t } = useTranslation(["signage", "premises"]);
  const { text, tone, detail } = liveState(t, props);
  const { fg, bg } = TONES[tone];
  return (
    <Tooltip title={detail} placement="right">
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        data-testid="signage-live-badge"
        sx={{
          alignSelf: "flex-start",
          px: 0.9,
          py: 0.3,
          borderRadius: "999px",
          bgcolor: bg,
          cursor: "default",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: fg,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="micro"
          sx={{
            fontWeight: "fontWeightBold",
            letterSpacing: "0.08em",
            color: fg,
          }}
        >
          {text}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
